import { useQuery, useMutation } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
import { zodParseWithLog } from "src/api/apiErrorLogs.ts";
import { useSnackbar } from "src/utils/useSnackbar";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { buildStorageFilename } from "src/components/QuestionnaireForm/FormProperty/context/utils/attachmentFilename";

const commentSchema = z.object({
    id: z.string(),
    comment: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    createdBy: z.object({
        firstname: z.string(),
        lastname: z.string(),
    }),
});

const attachmentSchema = z.object({
    attachmentPath: z.string(),
    createdAt: z.string(),
    id: z.string(),
    updatedAt: z.string(),
});

type PresignedUrlResponse = {
    presignedUrl: string;
    attachmentPath: string;
};

const presignedUrlResponseSchema: z.ZodSchema<PresignedUrlResponse> = z.object({
    presignedUrl: z.string(),
    attachmentPath: z.string(),
});

export const getOneAttachmentKey = "getOneAttachment" as const;

type SaveCommentOptions = {
    comment: string;
    ssqPostElementId: number;
};

enum ActionEnum {
    GET = "GET",
    PUT = "PUT",
    DELETE = "DELETE",
}

type getPresignedUrlOptions = {
    action: ActionEnum;
    filename: string;
    ssqPostElementId: number;
    // File size in bytes, sent on PUT so the backend can reject oversized attachments with a 4xx.
    fileSize?: number;
};

type saveAttachmentOptions = {
    ssqPostElementId: number;
    file: File;
};

export type UploadedAttachment = {
    id: string;
    filename: string;
    displayName: string;
};

export const uploadAttachmentFile = async (options: saveAttachmentOptions): Promise<UploadedAttachment> => {
    const { ssqPostElementId, file } = options;
    const attachmentId = crypto.randomUUID();
    const storageFilename = buildStorageFilename(attachmentId, file.name);
    const { presignedUrl, attachmentPath } = await getPresignedUrl({
        action: ActionEnum.PUT,
        filename: storageFilename,
        ssqPostElementId,
        fileSize: file.size,
    });
    await axios.put(presignedUrl, file, {
        headers: {
            "Content-Type": file.type,
        },
    });
    await loggedInApiCall.post(
        `/api/diagnostic/ssq-post_attachment`,
        {
            id: attachmentId,
            attachmentPath,
            ssqPostElement: `/api/ssq-post-elements/${ssqPostElementId}`,
        },
        {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        },
    );

    return {
        id: attachmentId,
        filename: storageFilename,
        displayName: file.name,
    };
};

type deleteAttachmentOptions = {
    ssqPostElementId: number;
    filename: string;
    attachmentId: string;
};

export const getSsqPostElement = "getSsqPostElement" as const;
const getAttachmentFile = "getAttachmentFile" as const;
const getPresignedUrlKey = "getPresignedUrl" as const;

export const useSaveComment = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: async (options: SaveCommentOptions) => {
            const { comment, ssqPostElementId } = options;
            await loggedInApiCall.post(`/api/diagnostic/ssq-post_comment`, {
                comment: comment,
                ssqPostElement: `/api/ssq-post-elements/${ssqPostElementId}`,
            });
            return true;
        },
        onError: (error) => {
            snackbar.setSnackbarProps({
                type: "error",
                message: t("property.note.save_error"),
            });
        },
    });
};

export const useDeleteComment = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: async (commentId: string) => {
            await loggedInApiCall.delete(`/api/diagnostic/ssq-post_comment/${commentId}`);
            return true;
        },
        onError: (error) => {
            console.error("Note deletion failed : ", error);
            snackbar.setSnackbarProps({
                type: "error",
                message: t("property.note.deletion_error"),
            });
        },
        onSettled: () => {},
    });
};

export const getPresignedUrl = async (options: getPresignedUrlOptions): Promise<PresignedUrlResponse> => {
    const { action, filename, ssqPostElementId, fileSize } = options;
    const url = `/api/diagnostic/questionnaire/documents/s3/generate_presigned_url`;
    const urlDataResponse = await loggedInApiCall.get(url, {
        headers: { Accept: "application/json" },
        params: { action, filename, ssqPostElementId, ...(fileSize !== undefined && { fileSize }) },
    });
    const parsedUrlData: PresignedUrlResponse = zodParseWithLog(
        urlDataResponse.data,
        presignedUrlResponseSchema,
        getPresignedUrlKey,
    );

    return parsedUrlData;
};

export const useSaveAttachment = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: uploadAttachmentFile,
        onSuccess: () => {
            snackbar.setSnackbarProps({
                message: t("property.attachment.upload_success", "Attached file uploaded successfully"),
                type: "success",
            });
        },
        onError: (error) => {
            console.error("S3 file upload failed : ", error);
            snackbar.setSnackbarProps({
                type: "error",
                message: t("property.attachment.upload_error", "An error occurred while uploading the file"),
            });
        },
    });
};

export const useDeleteAttachment = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: async (options: deleteAttachmentOptions) => {
            // 1 : get presigned URL
            const { ssqPostElementId, filename, attachmentId } = options;
            const urlData = await getPresignedUrl({
                action: ActionEnum.DELETE,
                filename,
                ssqPostElementId,
            });
            const presignedUrl = urlData.presignedUrl;
            // 2 : delete file from S3
            await axios.delete(presignedUrl);
            // 3 : delete Path from SSQ Post Element
            const url = `/api/diagnostic/ssq-post_attachment/${attachmentId}`;
            await loggedInApiCall.delete(url);
            return true;
        },
        onSuccess: () => {
            snackbar.setSnackbarProps({
                message: t("property.attachment.deletion_success", "Attached file deleted successfully"),
                type: "success",
            });
        },
        onError: (error) => {
            console.error("S3 file deletions failed : ", error);
            snackbar.setSnackbarProps({
                type: "error",
                message: t("property.attachment.deletion_error", "An error occurred while deleting the file"),
            });
        },
    });
};

type fecthAttachmentOptions = {
    ssqPostElementId: number | null;
    filename: string | undefined;
};

export const downloadAttachmentFile = async (ssqPostElementId: number, filename: string): Promise<File> => {
    const urlData = await getPresignedUrl({
        action: ActionEnum.GET,
        filename,
        ssqPostElementId,
    });
    const fileResponse = await axios.get(urlData.presignedUrl, { responseType: "blob" });
    const blob = fileResponse.data;

    return new File([blob], filename, { type: blob.type });
};

export const useFetchFile = (options: fecthAttachmentOptions) => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const { ssqPostElementId, filename } = options;

    return useQuery({
        queryKey: [getAttachmentFile, filename, ssqPostElementId],
        enabled: ssqPostElementId !== null && filename !== undefined,
        queryFn: async () => {
            if (ssqPostElementId === null) {
                console.error("No post Element Id provided");
                return;
            }
            if (filename === undefined) {
                console.error("No filename provided");
                return;
            }

            return downloadAttachmentFile(ssqPostElementId, filename);
        },
        onError: (error) => {
            console.error("Error fetching file", error);
            snackbar.setSnackbarProps({
                type: "error",
                message: t("property.attachment.fetching_error", "An error occurred while fetching the file"),
            });
        },
    });
};
